import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

export default function GuestMessageCenter({ apiBaseUrl, guestToken, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unread, setUnread] = useState(0);
  const esRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!guestToken) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${apiBaseUrl}/guest/messages`, { params: { token: guestToken }});
      setMessages(data);
      setUnread(data.filter(m => !m.is_read).length);
    } catch (e) {
      setError('Failed to load messages');
    } finally { setLoading(false); }
  }, [guestToken, apiBaseUrl]);

  // SSE subscription
  useEffect(() => {
    if (!guestToken) return;
    const url = `${apiBaseUrl.replace(/\/$/,'')}/guest/stream?token=${encodeURIComponent(guestToken)}`;
    const es = new EventSource(url); esRef.current = es;
    es.addEventListener('message.new', (ev) => {
      try {
        const parsed = JSON.parse(ev.data);
        setMessages(prev => [ { id: parsed.payload.id, channel: parsed.payload.channel, message_text: parsed.payload.message_text, created_at: parsed.payload.created_at, is_read: false, metadata: parsed.payload.metadata }, ...prev ].slice(0,200));
        setUnread(u => u + 1);
      } catch(_){}
    });
    es.addEventListener('message.read', (ev) => {
      try {
        const parsed = JSON.parse(ev.data);
        const mid = parsed.payload.id;
        setMessages(prev => prev.map(m => m.id===mid? { ...m, is_read:true } : m));
        setUnread(prev => Math.max(0, prev - 1));
      } catch(_){}
    });
    es.onerror = () => {};
    return () => { es.close(); };
  }, [guestToken, apiBaseUrl]);

  useEffect(() => { loadMessages(); }, [guestToken, loadMessages]);

  const markAllRead = async () => {
    const ids = messages.filter(m => !m.is_read).map(m => m.id);
    if (ids.length === 0) return;
    try { await axios.post(`${apiBaseUrl}/guest/messages/mark-read`, { ids, token: guestToken }); } catch(_){ }
  };

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }); } catch(_) { return ts; }
  };

  return (
    <div style={{ position:'fixed', top:0, right:0, width:340, maxWidth:'100%', height:'100%', background:'#fff', boxShadow:'-2px 0 6px rgba(0,0,0,0.12)', zIndex:3000, display:'flex', flexDirection:'column' }}>
      <div style={{ padding:'12px 14px', borderBottom:'1px solid #eee', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <strong style={{ fontSize:'0.95rem' }}>📬 Messages</strong>
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-small" onClick={loadMessages} disabled={loading}>⟳</button>
          <button className="btn btn-small" onClick={markAllRead} disabled={loading || unread===0}>✓</button>
          <button className="btn btn-small" onClick={onClose}>✕</button>
        </div>
      </div>
      {error && <div style={{ padding:10, color:'#dc3545', fontSize:'0.75rem' }}>{error}</div>}
      {loading && <div style={{ padding:10, fontSize:'0.75rem' }}>Loading…</div>}
      <div style={{ flex:1, overflowY:'auto', padding:10, display:'flex', flexDirection:'column', gap:10 }}>
        {messages.length === 0 && !loading && (
          <div style={{ fontSize:'0.75rem', color:'#666', textAlign:'center', marginTop:30 }}>No messages yet.</div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{ border:'1px solid #eee', background:m.is_read? '#fafafa':'#f0f7ff', borderRadius:8, padding:'8px 10px', fontSize:'0.7rem', lineHeight:1.4, position:'relative' }}>
            {!m.is_read && <span style={{ position:'absolute', top:6, right:8, width:8, height:8, background:'#0d6efd', borderRadius:'50%' }}></span>}
            <div style={{ fontWeight:600, fontSize:'0.65rem', color:'#444', marginBottom:4 }}>{formatTime(m.created_at)}</div>
            <div style={{ whiteSpace:'pre-wrap' }}>{m.message_text}</div>
          </div>
        ))}
      </div>
      <div style={{ padding:10, borderTop:'1px solid #eee', fontSize:'0.55rem', color:'#777' }}>
        Live updates enabled. Close this panel to dismiss.
      </div>
    </div>
  );
}
